/* eslint-disable no-unused-expressions */
import { Dialog, Transition } from '@headlessui/react';
import { graphql, useStaticQuery } from 'gatsby';
import PropTypes from 'prop-types';
import React, { Fragment, memo, useState } from 'react';
import { useAppContext } from '../../../contexts/AppProvider';
import { pluralize } from '../../../helpers/string';
import CloseIcon from '../../../images/icon-close.inline.svg';
import { getCartItemNumber } from '../../../utils/cart';
import { AddedProduct } from './added-product';
import { FeaturedProducts } from './featured-products';
import useProductData from '../../../utils/check-latest-stock-for-cart';
import { OrderTotal } from './order-total';
import { PerfectAdditions } from './perfect-addtions';
import toast from 'react-hot-toast';

/**
 * @description Render memoized cart modal component
 * @param {Object} props
 * @returns {React.Component} Memoized cart modal component
 */
export const CartModal = memo(function MemoizedCartModal(props) {
  const { open, closeModal } = props;
  const [checkoutResult, setCheckoutResult] = useState({ login_url: '', checkout_url: '', responseReceived: false });

  let related_products = [];
  let product = null;
  const [loading, setLoading] = useState(false);

  const appContext = useAppContext();
  const cartData =
    appContext &&
    Object.prototype.toString.call(appContext) === '[object Object]' &&
    Object.keys(appContext)?.length > 0
      ? appContext.cartData
      : null;
  const cart =
    cartData && Object.prototype.toString.call(cartData) === '[object Object]' && Object.keys(cartData)?.length > 0
      ? cartData.cart
      : null;
  const line_items =
    cart && Object.prototype.toString.call(cart) === '[object Object]' && Object.keys(cart)?.length > 0
      ? cart.line_items
      : null;
  const physical_items =
    line_items &&
    Object.prototype.toString.call(line_items) === '[object Object]' &&
    Object.keys(line_items)?.length > 0
      ? line_items.physical_items?.filter((item) => item.list_price !== 0)
      : null;
  const line_item =
    physical_items && Array.isArray(physical_items) && physical_items?.length > 0
      ? physical_items[physical_items.length - 1]
      : null;
  const cartNum =
    line_items &&
    Object.prototype.toString.call(line_items) === '[object Object]' &&
    Object.keys(line_items)?.length > 0
      ? getCartItemNumber(line_items)
      : null;

  const data = useStaticQuery(
    graphql`
      {
        allBigCommerceProducts(
          filter: { is_visible: { eq: true }, availability: { eq: "available" } }
          sort: { name: ASC }
        ) {
          nodes {
            id
            bigcommerce_id
            name
            custom_url {
              url
            }
            categories
            brand_id
            related_products
            calculated_price
            custom_fields {
              id
              name
              value
            }
            images {
              description
              id
              image_file
              is_thumbnail
              product_id
              sort_order
              url_thumbnail
              url_standard
              url_tiny
              url_zoom
            }
            options {
              display_name
              id
              name
              option_values {
                id
                is_default
                label
                sort_order
                value_data {
                  colors
                  image_url
                  product_id
                }
              }
              type
              sort_order
              product_id
            }
          }
        }
        allBigCommerceCategories(
          sort: { sort_order: ASC }
          filter: { is_visible: { eq: true }, image_url: { ne: "" }, parent_id: { ne: 0 } }
        ) {
          edges {
            node {
              bigcommerce_id
              custom_url {
                url
              }
              image_url
              name
              parent_id
              sort_order
            }
          }
        }
      }
    `
  );
  const products = data?.allBigCommerceProducts?.nodes || null;

  if (
    line_item &&
    Object.prototype.toString.call(line_item) === '[object Object]' &&
    Object.keys(line_item)?.length > 0 &&
    products &&
    Array.isArray(products) &&
    products?.length > 0
  ) {
    products.forEach((item) => (item.bigcommerce_id === line_item.product_id ? (product = item) : null));
  }

  const productRelatedProducts =
      product && Object.prototype.toString.call(product) === '[object Object]' && Object.keys(product)?.length > 0
        ? product.related_products
        : [],
    categories =
      product && Object.prototype.toString.call(product) === '[object Object]' && Object.keys(product)?.length > 0
        ? product.categories
        : [];

  if (
    products &&
    Array.isArray(products) &&
    products.length > 0 &&
    productRelatedProducts &&
    Array.isArray(productRelatedProducts) &&
    productRelatedProducts.length > 0
  ) {
    related_products =
      products.filter((node) => (productRelatedProducts.includes(node.bigcommerce_id) ? node : null)) || null;

    products.forEach((node) => {
      let match = false;

      node.categories && Array.isArray(node.categories) && node.categories?.length > 0
        ? node.categories.map((category) => (categories.includes(category) ? (match = true) : null))
        : null;
      match && related_products && related_products.length < 2 && node.bigcommerce_id !== product.bigcommerce_id
        ? related_products.push(node)
        : null;
    });
  }

  const allCategories = data?.allBigCommerceCategories?.edges || null;

  const onGoCheckout = async () => {
    setLoading(true);
    try {
      const { isOutOfStock } = await useProductData(line_items);
      setLoading(false);
      if (!isOutOfStock) {
        const { login_url, checkout_url } = await appContext.createCheckoutUrl();
        setCheckoutResult({
          responseReceived: true,
          login_url,
          checkout_url
        });
      }
    } catch (e) {
      setLoading(true);
      toast('Something went wrong!');
    }
    return;
  };

  const loginIframeLoaded = () => {
    goToCheckout();
  };

  const goToCheckout = () => {
    if (checkoutResult?.checkout_url) {
      closeModal();
      window.open(checkoutResult.checkout_url, '_self');
    }
  };

  const checkOutLogic = () => {
    if (checkoutResult?.login_url) {
      return <iframe className="hidden" src={checkoutResult.login_url} title="login" ref={loginIframeLoaded} />;
    } else {
      goToCheckout();
    }
  };

  return (
    <>
      {checkoutResult.responseReceived && checkOutLogic()}
      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" open={open} className="relative z-[101]" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-[1200px] max-w-full transform overflow-hidden bg-white text-left align-middle shadow-xl transition-all">
                  <div className="dialog-hedaer relative border-b-2 border-gray-200 py-6 text-center">
                    <CloseIcon className="absolute right-4 top-4 w-5 cursor-pointer" onClick={closeModal} />
                    <p className="mx-auto w-2/3 text-base font-bold md:w-full md:text-xl">{`OK, ${pluralize(
                      cartNum,
                      'Item'
                    )} added to your cart, What's next?`}</p>
                  </div>
                  <div className="dialog-content px-6 py-4 md:px-12 md:py-10">
                    {line_item && <AddedProduct item={line_item} />}
                    <div className="mt-8 flex flex-col gap-4 md:flex-row md:gap-8">
                      <div className="w-full md:w-3/4">
                        <PerfectAdditions items={allCategories} />
                        <div className="block md:hidden">
                          <OrderTotal
                            onClose={closeModal}
                            cartTotal={cartData?.grand_total}
                            cartNum={cartNum}
                            goCheckout={onGoCheckout}
                            loading={loading}
                          />
                        </div>
                        <FeaturedProducts products={related_products} />
                      </div>
                      <div className="hidden w-1/4 md:block">
                        <OrderTotal
                          cartTotal={cartData?.grand_total}
                          cartNum={cartNum}
                          onClose={closeModal}
                          goCheckout={onGoCheckout}
                          loading={loading}
                        />
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
});

CartModal.propTypes = {
  closeModal: PropTypes.func,
  open: PropTypes.bool
};
